import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  TextField,
} from "@mui/material";
import Produto from "../Shared/Interfaces/Produto";
import { formatToBRL } from "../../Utils/utils";
import { CadastroService } from "../../Services/CadastrosService";
import { TipoCadastro } from "../Shared/Enums/TipoCadastro";
import DataItem from "../Shared/Interfaces/DataItem";

interface PedidoModalProps {
  open: boolean;
  onClose: () => void;
  onConcluir: (produtoEditado: Produto) => void;
  produto: Produto;
}

const ModalEditarProduto: React.FC<PedidoModalProps> = ({
  open,
  onClose,
  onConcluir,
  produto,
}) => {
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    width: { xs: "95vw", sm: 1000 },
    height: "auto",
    transform: "translate(-50%, -50%)",
    maxWidth: "90vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
  };

  const cadastroService = new CadastroService();
  const [medidas, setMedidas] = useState<DataItem[]>([]);
  const [marcas, setMarcas] = useState<DataItem[]>([]);
  const [tiposDePneus, setTiposDePneus] = useState<DataItem[]>([]);
  const [tiposDeProdutos, setTiposDeProdutos] = useState<DataItem[]>([]);

  // Create a state to manage the product being edited
  const [produtoEdicao, setProdutoEdicao] = useState<Produto>({ ...produto });

  // Use useEffect to reset produtoEdicao state whenever a new produto is passed
  useEffect(() => {
    setProdutoEdicao({ ...produto });
  }, [produto]);

  // Load cadastros when component mounts
  useEffect(() => {
    getAllCadastros();
  }, []);

  const getMedidas = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Medidas}`
      );
      setMedidas(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getTipoDePneu = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDePneus}`
      );
      setTiposDePneus(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getTipoDeProduto = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDeProdutos}`
      );
      setTiposDeProdutos(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getMarcas = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Marcas}`
      );
      setMarcas(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getAllCadastros = async () => {
    getMedidas();
    getTipoDePneu();
    getTipoDeProduto();
    getMarcas();
  };

  const handleInputChange = (fieldName: keyof Produto, value: any) => {
    if(fieldName == 'valor'){
      // Remove non-numeric characters (except for commas and periods)
    const unformattedValue = value.replace(/\D/g, '');

      // Convert to a number and divide by 100 for cents
    const numericValue = Number(unformattedValue) / 100;
    value = numericValue.toString();
 }
    setProdutoEdicao((prevProduto) => ({
      ...prevProduto,
      [fieldName]: value,
    }));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <h1>Editar Produto</h1>
        <Paper style={{ padding: 16 }}>
          <Grid container spacing={2}>
          <Grid item xs={6} md={12} sx={{ display: "flex", marginTop: "1em" }}>
              <Grid item md={5}>
                <TextField
                  sx={{ width: "22em" }}
                  label="Descrição"
                  variant="outlined"
                  size="small"
                  value={produtoEdicao.descricao}
                  onChange={(e) => handleInputChange("descricao", e.target.value)}
                />
              </Grid>
              <Grid item md={3}>
                <Autocomplete
                  disablePortal
                  options={medidas}
                  getOptionLabel={(option) => option.descricao}
                  size="small"
                  value={
                    medidas.find((option) => option.descricao === produtoEdicao.medida) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange("medida", newValue?.descricao || "")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Medida" />
                  )}
                />
              </Grid>
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <Autocomplete
                  disablePortal
                  options={tiposDePneus}
                  getOptionLabel={(option) => option.descricao}
                  size="small"
                  value={
                    tiposDePneus.find((option) => option.descricao === produtoEdicao.tipoPneu) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange("tipoPneu", newValue?.descricao || "")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Pneu" />
                  )}
                />
              </Grid>
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <Autocomplete
                  disablePortal
                  options={tiposDeProdutos}
                  getOptionLabel={(option) => option.descricao}
                  size="small"
                  value={
                    tiposDeProdutos.find((option) => option.descricao === produtoEdicao.tipoProduto) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange("tipoProduto", newValue?.descricao || "")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Produto" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6} md={12} sx={{ display: "flex", marginTop: "1em" }}>
              <Grid item md={6}>
                <Autocomplete
                  disablePortal
                  size="small"
                  options={marcas}
                  getOptionLabel={(option) => option.descricao}
                  value={
                    marcas.find((option) => option.descricao === produtoEdicao.marca) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange("marca", newValue?.descricao || "")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Marca" />
                  )}
                />
              </Grid>
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <TextField
                  label="Quantidade"
                  variant="outlined"
                  size="small"
                  value={produtoEdicao.quantidade ?? ""}
                  onChange={(e) =>
                    handleInputChange("quantidade", parseInt(e.target.value) || 0)
                  }
                />
              </Grid>
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <TextField
                  label="Valor"
                  variant="outlined"
                  size="small"
                  value={formatToBRL(produtoEdicao.valor)}
                  onChange={(e) =>
                    handleInputChange("valor", e.target.value)
                  }
                />
              </Grid>
              {/*
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <TextField
                  label="Código"
                  variant="outlined"
                  size="small"
                  value={produtoEdicao.codigo || ""}
                  onChange={(e) =>
                    handleInputChange("codigo", parseInt(e.target.value) || 0)
                  }
                />
              </Grid>
              <Grid item md={2} sx={{ marginLeft: "1em" }}>
                <TextField
                  label="Lote"
                  variant="outlined"
                  size="small"
                  value={produtoEdicao.lote || ""}
                  onChange={(e) =>
                    handleInputChange("lote", parseInt(e.target.value) || 0)
                  }
                />
              </Grid>
              */}
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button variant="contained" onClick={onClose}>
              Fechar
            </Button>
            <Button
              variant="contained"
              onClick={() => onConcluir(produtoEdicao)}
            >
              Concluir
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default ModalEditarProduto;
